import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout } from 'slice/userSlice';
import { useDispatch } from 'react-redux';
import { IoPersonCircleSharp } from 'react-icons/io5';
import { MdDashboard } from 'react-icons/md';
import { 
  FaBoxes, 
  FaStore, 
  FaFileAlt, 
  FaCommentAlt, 
  FaBars, 
  FaNewspaper, 
  FaCalendarAlt, 
  FaExternalLinkAlt, 
  FaChevronDown,
  FaAngleRight,
  FaHome
} from 'react-icons/fa';
import { RiMenuFoldLine, RiMenuUnfoldLine } from 'react-icons/ri';
import logo from '../asserts/img/logo.png';
import './Layout.css';
import Button from 'components/Button';

// Helper to compute breadcrumb trail from pathname
function getBreadcrumbs(pathname) {
  if (pathname === '/dashboard' || pathname === '/') {
    return [{ label: 'Dashboard', path: '/dashboard' }];
  }

  const crumbs = [{ label: 'Dashboard', path: '/dashboard' }];

  // Product Catalog
  if (pathname.startsWith('/categories')) {
    crumbs.push({ label: 'Product Catalog', path: '/categories' });
    crumbs.push({ label: 'Product Categories', path: '/categories' });
  } else if (pathname.startsWith('/attributes')) {
    crumbs.push({ label: 'Product Catalog', path: '/categories' });
    crumbs.push({ label: 'Attribute Manager', path: '/attributes' });
  } else if (pathname.startsWith('/products')) {
    crumbs.push({ label: 'Product Catalog', path: '/categories' });
    crumbs.push({ label: 'All Products', path: '/products' });
  }

  // Showrooms & Stores
  else if (pathname.startsWith('/dealer-store') || pathname.startsWith('/edit-dealer-store')) {
    crumbs.push({ label: 'Showrooms & Stores', path: '/dealer-store' });
    crumbs.push({ label: 'Dealer Stores', path: '/dealer-store' });
    if (pathname.startsWith('/edit-dealer-store')) {
      crumbs.push({ label: 'Edit Store', path: pathname });
    }
  } else if (pathname.startsWith('/showroom-categories')) {
    crumbs.push({ label: 'Showrooms & Stores', path: '/dealer-store' });
    crumbs.push({ label: 'Showroom Categories', path: '/showroom-categories' });
  }

  // Pages & Content
  else if (
    pathname.startsWith('/page-list') || 
    pathname.startsWith('/edit-home') || 
    pathname.startsWith('/edit-about') || 
    pathname.startsWith('/edit-faq') || 
    pathname.startsWith('/edit-privacy') || 
    pathname.startsWith('/edit-training') || 
    pathname.startsWith('/edit-partner') || 
    pathname.startsWith('/edit-store-locator') || 
    pathname.startsWith('/modify-page-content') || 
    pathname.startsWith('/edit-page')
  ) {
    crumbs.push({ label: 'Pages & Content', path: '/page-list' });
    crumbs.push({ label: 'Static Pages Manager', path: '/page-list' });
    if (pathname.startsWith('/edit-home') || pathname === '/edit-page/1') crumbs.push({ label: 'Edit Home Page', path: pathname });
    else if (pathname.startsWith('/edit-about') || pathname === '/edit-page/2') crumbs.push({ label: 'Edit About Page', path: pathname });
    else if (pathname.startsWith('/edit-faq') || pathname === '/edit-page/5') crumbs.push({ label: 'Edit FAQ Page', path: pathname });
    else if (pathname.startsWith('/edit-privacy') || pathname === '/edit-page/9') crumbs.push({ label: 'Edit Privacy Policy', path: pathname });
    else if (pathname.startsWith('/edit-training') || pathname === '/edit-page/12') crumbs.push({ label: 'Edit Training Programs', path: pathname });
    else if (pathname.startsWith('/edit-partner') || pathname === '/edit-page/13') crumbs.push({ label: 'Edit Partner Page', path: pathname });
    else if (pathname.startsWith('/edit-store-locator') || pathname === '/edit-page/14') crumbs.push({ label: 'Edit Store Locator', path: pathname });
    else if (pathname.startsWith('/modify-page-content')) crumbs.push({ label: 'Modify Page Content', path: pathname });
  } else if (pathname.startsWith('/widget-manager')) {
    crumbs.push({ label: 'Pages & Content', path: '/page-list' });
    crumbs.push({ label: 'Page & Home Widgets', path: '/widget-manager' });
  }

  // Blogs & Articles
  else if (pathname.startsWith('/blog-list') || pathname.startsWith('/modify-blog-content')) {
    crumbs.push({ label: 'Blogs & Articles', path: '/blog-list' });
    crumbs.push({ label: 'All Blogs', path: '/blog-list' });
    if (pathname.startsWith('/modify-blog-content')) crumbs.push({ label: 'Modify Content', path: pathname });
  } else if (pathname.startsWith('/add-blog') || pathname.startsWith('/edit-blog')) {
    crumbs.push({ label: 'Blogs & Articles', path: '/blog-list' });
    crumbs.push({ label: pathname.startsWith('/edit-blog') ? 'Edit Blog' : 'Add New Blog', path: pathname });
  }

  // Events & News
  else if (pathname.startsWith('/event-list') || pathname.startsWith('/edit-event')) {
    crumbs.push({ label: 'Events & News', path: '/event-list' });
    crumbs.push({ label: 'All Events', path: '/event-list' });
    if (pathname.startsWith('/edit-event')) crumbs.push({ label: 'Edit Event', path: pathname });
  } else if (pathname.startsWith('/add-event')) {
    crumbs.push({ label: 'Events & News', path: '/event-list' });
    crumbs.push({ label: 'Add New Event', path: '/add-event' });
  }

  // Testimonials
  else if (pathname.startsWith('/testimonial-list') || pathname.startsWith('/edit-testimonial')) {
    crumbs.push({ label: 'Testimonials', path: '/testimonial-list' });
    crumbs.push({ label: 'All Testimonials', path: '/testimonial-list' });
    if (pathname.startsWith('/edit-testimonial')) crumbs.push({ label: 'Edit Testimonial', path: pathname });
  } else if (pathname.startsWith('/add-testimonial')) {
    crumbs.push({ label: 'Testimonials', path: '/testimonial-list' });
    crumbs.push({ label: 'Add Testimonial', path: '/add-testimonial' });
  }

  return crumbs;
}

const Layout = ({ children }) => {
  const [toggled, setToggle] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const currentUrl = location.pathname;

  const toggleMenu = (menu) => {
    setOpenMenu(prev => (prev === menu ? null : menu));
  };

  const logoutHandler = async (e) => {
    e.preventDefault();
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userToken');
      sessionStorage.removeItem('userToken');
      dispatch(logout());
      navigate('/signin');
    } catch (err) {
      console.log(err);
    }
  };

  // Automatically keep parent menu open based on current active route
  useEffect(() => {
    const path = location.pathname;
    if (['/categories', '/attributes', '/products'].some(p => path.startsWith(p))) {
      setOpenMenu('productManagement');
    } else if (['/dealer-store', '/edit-dealer-store', '/showroom-categories'].some(p => path.startsWith(p))) {
      setOpenMenu('manageShowroom');
    } else if ([
      '/page-list', '/edit-home', '/edit-about', '/edit-faq', '/edit-privacy', 
      '/edit-advantage', '/edit-training', '/edit-partner', '/edit-store-locator', 
      '/modify-page-content', '/edit-page', '/widget-manager'
    ].some(p => path.startsWith(p))) {
      setOpenMenu('pageManager');
    } else if (['/add-blog', '/blog-list', '/edit-blog', '/modify-blog-content'].some(p => path.startsWith(p))) {
      setOpenMenu('blogManager');
    } else if (['/add-event', '/event-list', '/edit-event'].some(p => path.startsWith(p))) {
      setOpenMenu('eventManager');
    } else if (['/add-testimonial', '/testimonial-list', '/edit-testimonial'].some(p => path.startsWith(p))) {
      setOpenMenu('manageTestimonials');
    }
  }, [location.pathname]);

  const breadcrumbs = getBreadcrumbs(currentUrl);

  // Group active checkers
  const isDashboardActive = currentUrl === '/dashboard' || currentUrl === '/';
  const isProductsActive = ['/categories', '/attributes', '/products'].some(p => currentUrl.startsWith(p));
  const isShowroomsActive = ['/dealer-store', '/edit-dealer-store', '/showroom-categories'].some(p => currentUrl.startsWith(p));
  const isPagesActive = [
    '/page-list', '/edit-home', '/edit-about', '/edit-faq', '/edit-privacy', 
    '/edit-advantage', '/edit-training', '/edit-partner', '/edit-store-locator', 
    '/modify-page-content', '/edit-page', '/widget-manager'
  ].some(p => currentUrl.startsWith(p));
  const isBlogsActive = ['/add-blog', '/blog-list', '/edit-blog', '/modify-blog-content'].some(p => currentUrl.startsWith(p));
  const isEventsActive = ['/add-event', '/event-list', '/edit-event'].some(p => currentUrl.startsWith(p));
  const isTestimonialsActive = ['/add-testimonial', '/testimonial-list', '/edit-testimonial'].some(p => currentUrl.startsWith(p));

  return (
    <div id="wrapper">
      {/* Sidebar */}
      <ul className={`premium-sidebar ${toggled ? 'toggled' : ''}`}>
        <Link className="sidebar-brand text-decoration-none" to="/dashboard">
          <img src={logo} alt="Aristo Logo" />
        </Link>

        {/* 1. Dashboard */}
        <li className={`nav-item ${isDashboardActive ? 'active' : ''}`}>
          <Link className="nav-link" to="/dashboard">
            <MdDashboard size={20} />
            <span>Dashboard</span>
          </Link>
        </li>

        {/* 2. Product Catalog */}
        <li className={`nav-item ${isProductsActive ? 'active' : ''}`}>
          <div
            role="button"
            tabIndex={0}
            className="nav-link collapsed"
            onClick={() => toggleMenu('productManagement')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleMenu('productManagement'); }}
            style={{ cursor: 'pointer' }}
          >
            <FaBoxes size={19} />
            <span>Product Management</span>
            <FaChevronDown 
              className={`menu-arrow ${openMenu === 'productManagement' ? 'rotated' : ''}`} 
              size={12} 
            />
          </div>
          <div className={`collapse ${openMenu === 'productManagement' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link 
                className={`collapse-item ${currentUrl.startsWith('/categories') ? 'active-sub' : ''}`} 
                to="/categories"
              >
                Product Categories
              </Link>
              <Link 
                className={`collapse-item ${currentUrl.startsWith('/attributes') ? 'active-sub' : ''}`} 
                to="/attributes"
              >
                Attribute Manager
              </Link>
              <Link 
                className={`collapse-item ${currentUrl.startsWith('/products') ? 'active-sub' : ''}`} 
                to="/products"
              >
                All Products
              </Link>
            </div>
          </div>
        </li>

        {/* 3. Showrooms & Stores */}
        <li className={`nav-item ${isShowroomsActive ? 'active' : ''}`}>
          <div
            role="button"
            tabIndex={0}
            className="nav-link collapsed"
            onClick={() => toggleMenu('manageShowroom')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleMenu('manageShowroom'); }}
            style={{ cursor: 'pointer' }}
          >
            <FaStore size={19} />
            <span>Showrooms & Stores</span>
            <FaChevronDown 
              className={`menu-arrow ${openMenu === 'manageShowroom' ? 'rotated' : ''}`} 
              size={12} 
            />
          </div>
          <div className={`collapse ${openMenu === 'manageShowroom' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link 
                className={`collapse-item ${(currentUrl.startsWith('/dealer-store') || currentUrl.startsWith('/edit-dealer-store')) ? 'active-sub' : ''}`} 
                to="/dealer-store"
              >
                Dealer Stores
              </Link>
              <Link 
                className={`collapse-item ${currentUrl.startsWith('/showroom-categories') ? 'active-sub' : ''}`} 
                to="/showroom-categories"
              >
                Showroom Categories
              </Link>
            </div>
          </div>
        </li>

        {/* 4. Pages & Content */}
        <li className={`nav-item ${isPagesActive ? 'active' : ''}`}>
          <div
            role="button"
            tabIndex={0}
            className="nav-link collapsed"
            onClick={() => toggleMenu('pageManager')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleMenu('pageManager'); }}
            style={{ cursor: 'pointer' }}
          >
            <FaFileAlt size={19} />
            <span>Pages & Content</span>
            <FaChevronDown 
              className={`menu-arrow ${openMenu === 'pageManager' ? 'rotated' : ''}`} 
              size={12} 
            />
          </div>
          <div className={`collapse ${openMenu === 'pageManager' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link 
                className={`collapse-item ${(
                  currentUrl.startsWith('/page-list') || 
                  currentUrl.startsWith('/edit-home') || 
                  currentUrl.startsWith('/edit-about') || 
                  currentUrl.startsWith('/edit-faq') || 
                  currentUrl.startsWith('/edit-privacy') || 
                  currentUrl.startsWith('/edit-advantage') || 
                  currentUrl.startsWith('/edit-training') || 
                  currentUrl.startsWith('/edit-partner') || 
                  currentUrl.startsWith('/edit-store-locator') || 
                  currentUrl.startsWith('/modify-page-content') || 
                  currentUrl.startsWith('/edit-page')
                ) ? 'active-sub' : ''}`} 
                to="/page-list"
              >
                Static Pages Manager
              </Link>
              <Link 
                className={`collapse-item ${currentUrl.startsWith('/widget-manager') ? 'active-sub' : ''}`} 
                to="/widget-manager"
              >
                Page & Home Widgets
              </Link>
            </div>
          </div>
        </li>

        {/* 5. Blogs & Articles */}
        <li className={`nav-item ${isBlogsActive ? 'active' : ''}`}>
          <div
            role="button"
            tabIndex={0}
            className="nav-link collapsed"
            onClick={() => toggleMenu('blogManager')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleMenu('blogManager'); }}
            style={{ cursor: 'pointer' }}
          >
            <FaNewspaper size={19} />
            <span>Blogs & Articles</span>
            <FaChevronDown 
              className={`menu-arrow ${openMenu === 'blogManager' ? 'rotated' : ''}`} 
              size={12} 
            />
          </div>
          <div className={`collapse ${openMenu === 'blogManager' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link 
                className={`collapse-item ${(currentUrl.startsWith('/blog-list') || currentUrl.startsWith('/modify-blog-content')) ? 'active-sub' : ''}`} 
                to="/blog-list"
              >
                All Blogs
              </Link>
              <Link 
                className={`collapse-item ${(currentUrl.startsWith('/add-blog') || currentUrl.startsWith('/edit-blog')) ? 'active-sub' : ''}`} 
                to="/add-blog"
              >
                Add New Blog
              </Link>
            </div>
          </div>
        </li>

        {/* 6. Events & News */}
        <li className={`nav-item ${isEventsActive ? 'active' : ''}`}>
          <div
            role="button"
            tabIndex={0}
            className="nav-link collapsed"
            onClick={() => toggleMenu('eventManager')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleMenu('eventManager'); }}
            style={{ cursor: 'pointer' }}
          >
            <FaCalendarAlt size={19} />
            <span>Events & News</span>
            <FaChevronDown 
              className={`menu-arrow ${openMenu === 'eventManager' ? 'rotated' : ''}`} 
              size={12} 
            />
          </div>
          <div className={`collapse ${openMenu === 'eventManager' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link 
                className={`collapse-item ${(currentUrl.startsWith('/event-list') || currentUrl.startsWith('/edit-event')) ? 'active-sub' : ''}`} 
                to="/event-list"
              >
                All Events
              </Link>
              <Link 
                className={`collapse-item ${currentUrl.startsWith('/add-event') ? 'active-sub' : ''}`} 
                to="/add-event"
              >
                Add New Event
              </Link>
            </div>
          </div>
        </li>

        {/* 7. Manage Testimonials */}
        <li className={`nav-item ${isTestimonialsActive ? 'active' : ''}`}>
          <div
            role="button"
            tabIndex={0}
            className="nav-link collapsed"
            onClick={() => toggleMenu('manageTestimonials')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleMenu('manageTestimonials'); }}
            style={{ cursor: 'pointer' }}
          >
            <FaCommentAlt size={18} />
            <span>Testimonials</span>
            <FaChevronDown 
              className={`menu-arrow ${openMenu === 'manageTestimonials' ? 'rotated' : ''}`} 
              size={12} 
            />
          </div>
          <div className={`collapse ${openMenu === 'manageTestimonials' ? 'show' : ''}`}>
            <div className="collapse-inner">
              <Link 
                className={`collapse-item ${(currentUrl.startsWith('/testimonial-list') || currentUrl.startsWith('/edit-testimonial')) ? 'active-sub' : ''}`} 
                to="/testimonial-list"
              >
                All Testimonials
              </Link>
              <Link 
                className={`collapse-item ${currentUrl.startsWith('/add-testimonial') ? 'active-sub' : ''}`} 
                to="/add-testimonial"
              >
                Add Testimonial
              </Link>
            </div>
          </div>
        </li>

        {/* Sidebar Toggle Button */}
        <div className="text-center mt-5 mb-4 px-3">
          <button
            className="btn btn-outline-light w-100 rounded-pill"
            style={{ borderColor: 'rgba(196,168,130,0.3)', color: '#c4a882' }}
            onClick={() => setToggle(!toggled)}
          >
            {toggled ? <RiMenuUnfoldLine size={20} /> : <RiMenuFoldLine size={20} />}
          </button>
        </div>
      </ul>

      {/* Content Wrapper */}
      <div id="content-wrapper">
        <nav className="premium-topbar navbar navbar-expand navbar-light topbar">
          <div className="d-flex align-items-center" style={{ gap: '14px', flex: 1, minWidth: 0 }}>
            <button
              type="button"
              id="sidebarToggleTop"
              onClick={() => setToggle(!toggled)}
              className="sidebar-toggle-btn"
              title={toggled ? "Open Sidebar" : "Close Sidebar"}
            >
              <FaBars size={17} />
            </button>

            {/* Dynamic Breadcrumbs Navigation Trail */}
            <nav aria-label="breadcrumb" className="admin-breadcrumb-nav d-none d-sm-flex align-items-center">
              {breadcrumbs.map((crumb, idx) => {
                const isLast = idx === breadcrumbs.length - 1;
                return (
                  <span key={crumb.label + idx} className="breadcrumb-item-wrapper d-inline-flex align-items-center">
                    {idx > 0 && <FaAngleRight size={10} className="breadcrumb-separator mx-2" />}
                    {isLast ? (
                      <span className="breadcrumb-current">{crumb.label}</span>
                    ) : (
                      <Link to={crumb.path} className="breadcrumb-link">
                        {idx === 0 && <FaHome size={12} className="mr-1 mb-0.5" />}
                        {crumb.label}
                      </Link>
                    )}
                  </span>
                );
              })}
            </nav>
          </div>
          
          {/* Topbar Right Section */}
          <div className="d-flex align-items-center" style={{ gap: '14px' }}>
            {/* Live Website Shortcut Button */}
            <a
              href="http://localhost:3000"
              target="_blank"
              rel="noreferrer"
              className="btn-view-live-site d-none d-md-inline-flex"
              title="Open Live User-Facing Website in New Tab"
            >
              <span>Live Website</span>
              <FaExternalLinkAlt size={11} />
            </a>

            {/* User Profile / Logout Dropdown */}
            <ul className="navbar-nav ml-auto">
              <li className="nav-item dropdown no-arrow">
                <Link
                  className="nav-link dropdown-toggle"
                  to="#"
                  id="userDropdown"
                  role="button"
                  data-toggle="dropdown"
                  aria-haspopup="true"
                  aria-expanded="false"
                >
                  <span className="mr-2 d-none d-lg-inline admintext">Aristo Admin</span>
                  <IoPersonCircleSharp size={32} style={{ color: '#c4a882' }} />
                </Link>
                <div className="dropdown-menu dropdown-menu-right" aria-labelledby="userDropdown">
                  <Button className="dropdown-item w-100 text-left bg-transparent border-0" text="Logout" onclickfun={logoutHandler} />
                </div>
              </li>
            </ul>
          </div>
        </nav>
        
        <div id="content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
